import { useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

export interface AnimatedCounterProps {
  /** The final value to count up to. */
  value: number;
  className?: string;
  /** Decimal places to display (e.g. 1 for retention rates like 97.3). */
  decimals?: number;
  /** Static text before the number (e.g. '+', 'CHF '). */
  prefix?: string;
  /** Static text after the number (e.g. '%', '+', 'k'). */
  suffix?: string;
  duration?: number;
  /** Group thousands with a separator. */
  grouping?: boolean;
}

const formatValue = (n: number, decimals: number, grouping: boolean): string => {
  const fixed = n.toFixed(decimals);
  if (!grouping) return fixed;
  const [int, frac] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${grouped}.${frac}` : grouped;
};

/**
 * Counts up to a value when scrolled into view — the signature treatment for
 * IBAS proof points (graduate counts, retention rates, nationalities). Every
 * such figure must be sourced and, per the Claims Rule, carry year/scope
 * context nearby. Under reduced motion the final value is shown immediately.
 */
export function AnimatedCounter({
  value,
  className,
  decimals = 0,
  prefix = '',
  suffix = '',
  duration = DURATION.cinematic,
  grouping = true,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      if (reduced) {
        el.textContent = `${prefix}${formatValue(value, decimals, grouping)}${suffix}`;
        return;
      }
      const counter = { n: 0 };
      gsap.to(counter, {
        n: value,
        duration,
        ease: GSAP_EASE.out,
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        onUpdate: () => {
          el.textContent = `${prefix}${formatValue(counter.n, decimals, grouping)}${suffix}`;
        },
      });
    },
    { scope: ref, dependencies: [reduced, value, decimals, prefix, suffix, duration, grouping] },
  );

  return (
    <span ref={ref} className={cn('tabular-nums', className)}>
      {prefix}
      {formatValue(0, decimals, grouping)}
      {suffix}
    </span>
  );
}
